import { useCallback, useEffect, useRef, useState } from 'react';
import { avisar } from './Avisos.tsx';
import { Confirmar } from './Confirmar.tsx';
import { Icone } from './Icone.tsx';
import { ErroDaApi, apiMarketing, type AchadoNoPost, type AvisoDaArte, type PainelDasArtes, type PostDeMarketing } from '../lib/api.ts';
import { NOME_DA_REGRA } from '../lib/posts.ts';

/**
 * ⭐⭐ A MONTAGEM DAS ARTES — a parte 4 da aba Marketing, 16/10/2026 (PRD §50.7).
 *
 * O Hub põe sobre os fundos da parte 3 o texto da parte 2, o logotipo, a
 * numeração e, no carrossel, o fecho como último slide, no desenho das artes
 * que ele aprovou em 15/10/2026. A seção mostra a tira lado a lado e cada
 * slide, e entrega os arquivos: tudo num ZIP (os PNG e o texto para colar),
 * cada slide, e o PDF para o LinkedIn.
 *
 * - ⭐ **Montar com achado grave pergunta antes**, como aprovar; e quem
 *   pergunta é também o servidor (409), para nenhum caminho montar sem ela.
 * - ⭐ **Texto ou fundo que muda remonta sozinho**, quando não há o que
 *   perguntar (nenhum achado grave, nenhum fundo faltando). Enquanto a nova
 *   não fica pronta, a anterior continua na tela e no disco.
 * - ⛔ **O nome do arquivo que se baixa é dado aqui**, a partir do tema: o
 *   servidor não manda nome em cabeçalho.
 * - ⭐ **O PDF do LinkedIn aparece em todo carrossel**, mesmo no marcado só
 *   para o Instagram: "Quem somos" e "Os níveis" saíram assim e foram depois,
 *   em PDF, à página do LinkedIn.
 *
 * ⛔⛔ **Fala só com `/api/marketing`** (PRD §7.1).
 */

const quando = (iso: string) => {
  const d = new Date(iso);
  /* ⚠️ A hora da casa é a de São Paulo, qualquer que seja o relógio de quem abre. */
  const dia = new Intl.DateTimeFormat('pt-BR', { timeZone: 'America/Sao_Paulo', day: 'numeric', month: 'long', year: 'numeric' }).format(d);
  const hora = new Intl.DateTimeFormat('pt-BR', { timeZone: 'America/Sao_Paulo', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(d).replace(':', 'h');
  return `${dia}, às ${hora}`;
};

/** Os achados e avisos, um por linha, com o lugar. */
function ListaDeAvisos({ lista }: { lista: Array<AchadoNoPost | AvisoDaArte> }) {
  if (!lista.length) return null;
  return (
    <ul className="mkt-achados-do-campo mkt-artes-avisos">
      {lista.map((a, i) => (
        <li key={i} className={`mkt-achado ${a.gravidade}`}>
          <Icone nome="atencao" tamanho={13} />
          <span>
            <b>
              {a.gravidade === 'grave' ? 'Grave' : 'Atenção'} · {a.onde}
              {'regra' in a && a.regra !== 'arte' ? ` · ${NOME_DA_REGRA[a.regra] ?? a.regra}` : ''}.
            </b>{' '}
            {a.aviso}
            {'trecho' in a && a.trecho && <q>{a.trecho}</q>}
          </span>
        </li>
      ))}
    </ul>
  );
}

/** Baixa um arquivo da API pela sessão e dá a ele o nome escolhido aqui. */
async function baixar(endereco: string, nome: string) {
  const r = await fetch(endereco, { credentials: 'same-origin' });
  if (!r.ok) {
    const bruto = await r.text();
    let dito = bruto;
    try {
      dito = (JSON.parse(bruto) as { erro?: string }).erro ?? bruto;
    } catch {
      /* segue com o texto cru */
    }
    throw new ErroDaApi(dito || `O servidor respondeu ${r.status}.`, r.status);
  }
  const url = URL.createObjectURL(await r.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = nome;
  document.body.appendChild(a);
  a.click();
  a.remove();
  /* ⚠️ Solta a memória do arquivo depois que o navegador começou a salvar. */
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export function ArtesDoPost({
  post,
  versaoDoConteudo,
  versaoDosFundos,
  conteudoPorGravar,
  aoExpirarSessao,
}: {
  post: PostDeMarketing;
  /** Muda a cada gravação do conteúdo: as artes podem ter ficado desatualizadas. */
  versaoDoConteudo: number;
  /** Muda a cada fundo enviado, trocado ou removido. */
  versaoDosFundos: number;
  conteudoPorGravar: boolean;
  aoExpirarSessao: () => void;
}) {
  const [painel, setPainel] = useState<PainelDasArtes | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  /** `montar` (pela pessoa), `remontar` (sozinho), ou o arquivo que está baixando. */
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [erroDeMontar, setErroDeMontar] = useState<string | null>(null);
  const [pergunta, setPergunta] = useState<string | null>(null);
  /** O estado em que a remontagem sozinha já foi tentada: não tenta duas vezes a mesma coisa. */
  const tentouRemontar = useRef<string | null>(null);

  const falhou = useCallback(
    (e: unknown, padrao: string) => {
      if (e instanceof ErroDaApi && e.status === 401) {
        aoExpirarSessao();
        return null;
      }
      return e instanceof Error ? e.message : padrao;
    },
    [aoExpirarSessao],
  );

  const redes = post.redes.join(',');
  useEffect(() => {
    let vale = true;
    apiMarketing
      .artes(post.id)
      .then((p) => {
        if (!vale) return;
        setPainel(p);
        setErro(null);
      })
      .catch((e) => vale && setErro(falhou(e, 'Não consegui carregar as artes.')));
    return () => {
      vale = false;
    };
    /* O plano (formato, tema, redes), o texto gravado e os fundos mudam a montagem. */
  }, [post.id, post.formato, post.titulo, redes, versaoDoConteudo, versaoDosFundos, falhou]);

  const montar = useCallback(
    async (comAchados: boolean, sozinho = false) => {
      setOcupado(sozinho ? 'remontar' : 'montar');
      setErroDeMontar(null);
      try {
        const novo = await apiMarketing.montarArtes(post.id, comAchados);
        setPainel(novo);
        avisar(sozinho ? 'As artes foram montadas de novo com o texto e os fundos de agora.' : `Artes montadas: ${novo.montagem?.pecas ?? 0} imagens.`);
      } catch (e) {
        /* Com achado grave, o servidor pergunta; a tela repete a pergunta com o que ele disse. */
        if (e instanceof ErroDaApi && e.status === 409 && !sozinho && /achado/.test(e.message)) {
          setPergunta(e.message);
        } else {
          const m = falhou(e, 'Não consegui montar as artes.');
          if (m) setErroDeMontar(m);
        }
        /* Relê: o painel diz o que falta e o que mudou. */
        apiMarketing.artes(post.id).then(setPainel).catch(() => undefined);
      } finally {
        setOcupado(null);
      }
    },
    [post.id, falhou],
  );

  /*
   * ⭐ **A remontagem sozinha.** A montagem guardada não é mais a do texto e
   * dos fundos de agora, e não há o que perguntar: monta de novo. Com achado
   * grave ou fundo faltando, espera a pessoa (a seção diz por quê).
   */
  useEffect(() => {
    if (!painel || ocupado || conteudoPorGravar) return;
    const m = painel.montagem;
    if (!m || m.atual || painel.faltam.length || painel.graves.length) return;
    const chave = `${m.id}|${versaoDoConteudo}|${versaoDosFundos}|${post.formato}|${post.titulo}|${redes}`;
    if (tentouRemontar.current === chave) return;
    tentouRemontar.current = chave;
    void montar(false, true);
  }, [painel, ocupado, conteudoPorGravar, versaoDoConteudo, versaoDosFundos, post.formato, post.titulo, redes, montar]);

  if (erro) {
    return (
      <section className="s-folha mkt-artes" aria-label="Artes do post">
        <header className="s-folha-topo">
          <h2 className="s-folha-titulo">Artes</h2>
        </header>
        <p className="erro mkt-artes-corpo">{erro}</p>
      </section>
    );
  }
  if (!painel) return null;

  const m = painel.montagem;
  const temGrave = painel.graves.length > 0;
  const carrossel = painel.formato === 'carrossel';
  const podeMontar = painel.formato !== 'texto' && painel.pecas.length > 0 && painel.faltam.length === 0 && !ocupado;
  const lugares = [...new Set(painel.graves.map((a) => a.onde))];

  async function pedirMontagem() {
    if (conteudoPorGravar) return setErroDeMontar('O conteúdo tem alteração ainda não gravada. Grave antes de montar: o que se monta é o que está gravado.');
    if (temGrave) {
      return setPergunta(
        `A conferência tem ${painel!.graves.length === 1 ? 'um achado grave' : `${painel!.graves.length} achados graves`} neste post. ` +
          `Onde: ${lugares.slice(0, 3).join('; ')}${lugares.length > 3 ? ' e mais' : ''}.`,
      );
    }
    await montar(false);
  }

  async function baixarArquivo(qual: string, endereco: string, nome: string, mensagem: string) {
    setOcupado(qual);
    try {
      await baixar(endereco, nome);
      avisar(mensagem);
    } catch (e) {
      const msg = falhou(e, 'Não consegui baixar o arquivo.');
      if (msg) avisar(msg, 'ruim');
    } finally {
      setOcupado(null);
    }
  }

  return (
    <section className="s-folha mkt-artes" aria-label="Artes do post">
      <header className="s-folha-topo mkt-conteudo-topo">
        <div>
          <h2 className="s-folha-titulo">Artes</h2>
          <p className="mkt-nota">O Hub põe sobre os fundos o texto, o logotipo, a numeração e o fecho, no desenho da casa.</p>
        </div>
        {painel.formato !== 'texto' && (
          <div className="mkt-artes-botoes">
            {m && (
              <>
                <button
                  type="button"
                  className="imv-bt"
                  disabled={Boolean(ocupado)}
                  onClick={() => void baixarArquivo('zip', apiMarketing.zipDasArtes(post.id), `${painel.nome}.zip`, 'ZIP baixado: as imagens e o texto para colar.')}
                >
                  <Icone nome="baixar" tamanho={14} /> {ocupado === 'zip' ? 'Preparando…' : 'Baixar tudo'}
                </button>
                {/* ⭐ Em todo carrossel, e não só no marcado para o LinkedIn: os dois de 15/10/2026 foram ao ar só no Instagram e depois, em PDF, à página do LinkedIn. */}
                {carrossel && (
                  <button
                    type="button"
                    className="imv-bt"
                    disabled={Boolean(ocupado)}
                    title="O mesmo carrossel em PDF, um slide por página: é como o LinkedIn publica documento."
                    onClick={() => void baixarArquivo('pdf', apiMarketing.pdfDasArtes(post.id), `${painel.nome}-linkedin.pdf`, 'PDF do LinkedIn baixado.')}
                  >
                    <Icone nome="linkedin" tamanho={14} /> {ocupado === 'pdf' ? 'Preparando…' : 'PDF para o LinkedIn'}
                  </button>
                )}
              </>
            )}
            <button type="button" className="imv-bt primario" disabled={!podeMontar} onClick={() => void pedirMontagem()}>
              <Icone nome="tela" tamanho={14} /> {ocupado === 'montar' ? 'Montando…' : m ? 'Montar de novo' : 'Montar as artes'}
            </button>
          </div>
        )}
      </header>

      <div className="mkt-artes-corpo">
        {painel.formato === 'texto' ? (
          <p className="mkt-vazio-bloco">Post só de texto: não leva arte.</p>
        ) : (
          <>
            {/* O estado da montagem, em uma linha. */}
            {m ? (
              <p className={`mkt-artes-estado${m.atual ? ' em-dia' : ''}`}>
                <Icone nome={m.atual ? 'concluido' : 'relogio'} tamanho={14} />
                <span>
                  {ocupado === 'remontar'
                    ? 'O texto ou um fundo mudou: montando de novo. As artes abaixo ficam até as novas ficarem prontas.'
                    : m.atual
                      ? `${m.pecas} ${m.pecas === 1 ? 'imagem montada' : 'imagens montadas'} em ${quando(m.montadaEm)}, por ${m.montadaPor.nome}. São as do texto e dos fundos de agora.`
                      : `Montadas em ${quando(m.montadaEm)}. O texto ou um fundo mudou depois disso: estas artes não são as de agora.`}
                  {m.comAchadosGraves && !ocupado && ' Foram montadas com achado grave, depois da pergunta.'}
                </span>
              </p>
            ) : (
              <p className="mkt-vazio-bloco">
                {painel.pecas.length ? `Ainda não montadas. Serão ${painel.pecas.length} ${painel.pecas.length === 1 ? 'imagem' : 'imagens'}${carrossel ? ', com o fecho como última' : ''}.` : 'Grave os slides do conteúdo antes: as artes saem deles.'}
              </p>
            )}

            {conteudoPorGravar && <p className="mkt-aviso">Há texto do conteúdo por gravar. A montagem usa o que está gravado.</p>}
            {painel.faltam.length > 0 && painel.pecas.length > 0 && (
              <p className="mkt-aviso">
                Faltam os fundos de: {painel.faltam.join(', ')}. Envie as imagens na seção Fundos, acima: sem elas, não se monta.
              </p>
            )}
            {m && !m.atual && !ocupado && (temGrave || painel.faltam.length > 0) && (
              <p className="mkt-aviso">{temGrave ? 'Há achado grave: o Hub não monta de novo sozinho. Confira, ou use "Montar de novo".' : 'Quando os fundos que faltam chegarem, o Hub monta de novo sozinho.'}</p>
            )}
            {erroDeMontar && <p className="erro">{erroDeMontar}</p>}

            {/*
              ⭐ Os avisos: com a montagem em dia, uma lista só (os achados graves de agora e o que a
              montagem guardou, que já traz o texto que não cabe e as emendas). Sem ela, o que se vê
              antes de montar e, se houver, o que a montagem anterior avisou.
            */}
            {m?.atual ? (
              (painel.graves.some((a) => a.regra !== 'arte') || m.avisos.length > 0) && (
                <div className="mkt-artes-bloco">
                  <h3 className="mkt-bloco-titulo">Avisos</h3>
                  <ListaDeAvisos lista={[...painel.graves.filter((a) => a.regra !== 'arte'), ...m.avisos]} />
                </div>
              )
            ) : (
              <>
                {(painel.graves.length > 0 || painel.avisos.length > 0) && (
                  <div className="mkt-artes-bloco">
                    <h3 className="mkt-bloco-titulo">Antes de montar</h3>
                    <ListaDeAvisos lista={[...painel.graves, ...painel.avisos]} />
                  </div>
                )}
                {m && m.avisos.length > 0 && (
                  <div className="mkt-artes-bloco">
                    <h3 className="mkt-bloco-titulo">Na montagem anterior</h3>
                    <ListaDeAvisos lista={m.avisos} />
                  </div>
                )}
              </>
            )}

            {m && (
              <>

                {/* ⭐ A tira: as artes lado a lado, como o carrossel corre. */}
                <div className="mkt-artes-bloco">
                  <h3 className="mkt-bloco-titulo">{carrossel ? 'O carrossel, lado a lado' : 'A imagem'}</h3>
                  <div className={`mkt-artes-tira${ocupado === 'remontar' ? ' velha' : ''}`} role="img" aria-label={`As ${m.pecas} artes, lado a lado, na ordem em que vão ao ar`}>
                    {m.nomes.map((nome, i) => (
                      <img key={nome + i} src={apiMarketing.imagemDaArte(post.id, i + 1, m.id)} alt="" width={1080} height={1350} />
                    ))}
                  </div>
                </div>

                {/* E cada uma, com o botão de baixar. */}
                <ol className="mkt-artes-lista">
                  {m.nomes.map((nome, i) => {
                    const url = apiMarketing.imagemDaArte(post.id, i + 1, m.id);
                    return (
                      <li key={nome + i} className="mkt-arte">
                        <a href={url} target="_blank" rel="noopener" title="Abrir em tamanho real">
                          <img src={url} alt={`Arte ${i + 1}: ${nome}`} width={1080} height={1350} loading="lazy" />
                        </a>
                        <div className="mkt-arte-pe">
                          <span className="mkt-slide-num dado">{i + 1}</span>
                          <b>{nome}</b>
                          <button
                            type="button"
                            className="imv-bt fantasma p"
                            disabled={Boolean(ocupado)}
                            aria-label={`Baixar a arte ${i + 1}: ${nome}`}
                            title="Baixar esta arte"
                            onClick={() => void baixarArquivo(`png${i}`, url, `${painel.nome}-${i + 1}.png`, `${nome} baixada.`)}
                          >
                            <Icone nome="baixar" tamanho={12} /> <span className="mkt-arte-baixar-texto">Baixar</span>
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ol>
              </>
            )}
          </>
        )}
      </div>

      {pergunta && (
        <Confirmar
          titulo="Montar com achado grave"
          descricao={`${pergunta} A arte sai com o texto como está gravado. Para corrigir antes, cancele e ajuste o conteúdo.`}
          rotuloAcao="Montar assim mesmo"
          tom="primario"
          aoFechar={() => setPergunta(null)}
          aoConfirmar={() => {
            setPergunta(null);
            void montar(true);
          }}
        />
      )}
    </section>
  );
}
